// ============================================================================
// TeamsTab — onglet EQUIPES de l'ecran Admin (AD-B)
// ============================================================================
// Porte du PoC components/admin/teams-section.tsx aux standards du parc : la
// grille de cartes PoC devient une DataTable @gs/ui (releve Fable — les tables
// brutes du PoC deviennent DataTable), creation/edition via TeamFormDialog
// (GsWizardDialog AD-0). Le clic de ligne OUVRE l'edition (drill-in standard #2
// — ici l'action attendue de l'admin). Erreur mutation -> alerte i18n locale
// (lecon GA-21 : jamais de code brut a l'ecran).
// Le pool assignable vient du bundle serveur (ce/cg + directors actuels) —
// STOP-8 : l'affectation est decouplee du role UCBR.
// Lot 3 (2026-07-26) : presentation cartes via TeamOverviewCard partagee
// (General > Equipes) — capacites admin conservees (edition, drill-in manage).

import { useState } from 'react';
import { Plus, Users } from 'lucide-react';
import { EmptyState, SkeletonCard, QueryError, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import {
  useCreateTeam, useUpdateTeam, adminTeamErrorCode,
  type AdminTeamsResponse, type AdminTeamSummary, type TeamUpdatePayload,
} from '../../hooks/useAdminTeams';
import { TeamOverviewCard } from '../equipes/TeamOverviewCard';
import { SectionHeader } from './SectionHeader';
import { TeamFormDialog, type TeamFormValues } from './TeamFormDialog';

// `initial` est FIGE au moment du clic (objet stable) : le passer par l'etat
// evite qu'un re-render re-seed le formulaire pendant la saisie (le useEffect du
// dialog depend de l'identite de `initial`).
type DialogState =
  | { mode: 'create' }
  | { mode: 'edit'; team: AdminTeamSummary; initial: TeamFormValues }
  | null;

function initialValuesFor(team: AdminTeamSummary): TeamFormValues {
  return {
    name: team.name,
    department: team.department ?? '',
    headcount: String(team.headcount),
    directorEmail: team.directorEmail ?? '',
  };
}

export function TeamsTab({
  isLoading,
  isError,
  data,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  data: AdminTeamsResponse | undefined;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (isError || !data) {
    return <QueryError message={t('gest:admin.teams.loadError')} onRetry={onRetry} />;
  }

  const pending = createTeam.isPending || updateTeam.isPending;

  function submit(payload: TeamUpdatePayload) {
    setErrorCode(null);
    const done = {
      onSuccess: () => setDialog(null),
      onError: (err: unknown) => {
        setDialog(null);
        setErrorCode(adminTeamErrorCode(err));
      },
    };
    if (dialog?.mode === 'edit') {
      updateTeam.mutate({ id: dialog.team.id, payload }, done);
    } else {
      // Creation : director NULL a la creation (parite PoC) — payload reduit.
      createTeam.mutate(
        { name: payload.name, department: payload.department, headcount: payload.headcount },
        done,
      );
    }
  }

  function openEdit(team: AdminTeamSummary) {
    setErrorCode(null);
    setDialog({ mode: 'edit', team, initial: initialValuesFor(team) });
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title={t('gest:admin.teams.title')}
        description={t('gest:admin.teams.description')}
      >
        <span className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.teams.count', { n: data.teams.length })}
        </span>
        <button
          type="button"
          onClick={() => { setErrorCode(null); setDialog({ mode: 'create' }); }}
          className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-1.5`}
        >
          <Plus size={14} aria-hidden="true" />
          {t('gest:admin.teams.create')}
        </button>
      </SectionHeader>

      {errorCode ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">
          {t(`gest:admin.teams.errors.${errorCode}`)}
        </p>
      ) : null}

      {data.teams.length === 0 ? (
        <EmptyState
          icon={<Users size={48} aria-hidden="true" />}
          title={t('gest:admin.teams.empty')}
          description={t('gest:admin.teams.emptyHint')}
        />
      ) : (
        <div
          className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3"
          role="list"
        >
          {data.teams.map((team) => (
            <TeamOverviewCard
              key={team.id}
              team={team}
              to={`/admin/teams/${team.id}`}
              onEdit={() => openEdit(team)}
              editLabel={t('gest:admin.teams.editMeta', { name: team.name })}
            />
          ))}
        </div>
      )}

      <TeamFormDialog
        open={dialog != null}
        mode={dialog?.mode ?? 'create'}
        initial={dialog?.mode === 'edit' ? dialog.initial : undefined}
        assignable={data.assignable}
        isPending={pending}
        onClose={() => setDialog(null)}
        onSubmit={submit}
      />
    </div>
  );
}
